import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type UserResource,
  UsersController_create_v1,
  type UsersController_create_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { optionalPinSchema, staffCodeSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { PasswordInput } from '@/routes/PasswordInput';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import {
  DEFAULT_NEW_USER_ROLE,
  hintForRole,
  labelForRole,
  signsInAtTheDesk,
  USER_ROLE_ITEMS,
  USER_ROLES,
  type UserRole,
} from './user-roles';

/**
 * Creating a person: `POST /v1/users`, three fields and two optional
 * credentials.
 *
 * **Two refusals, two fields.** `IDENTITY_STAFF_CODE_TAKEN` is a statement about
 * the staff code and `IDENTITY_ROLE_ESCALATION` is a statement about the role,
 * and each is rendered against the input it concerns. The difference between
 * that and one banner above the submit is the difference between correcting one
 * control and re-reading five to work out which one the server meant.
 * `docs/ui-conventions.md` asks for errors beside the field they concern; a form
 * with two field-specific refusals is the case that makes the rule worth having.
 *
 * Everything else the API can refuse with goes above the submit, because nothing
 * else here is about one field — a lapsed session, a role that does not permit
 * this at all, a network that went away.
 *
 * **The copy is never written here.** `messageForFailure` turns the code into a
 * sentence and it is the only thing that does; the code is what is branched on,
 * never the message and never the status. A 409 from a proxy is not this
 * refusal.
 *
 * **No `outletIds` field.** The ticket describes one on this route and the
 * shipped contract does not have one: the API's own `CreateUserDto` says in as
 * many words that it omits `outletIds` because assignments are a separate
 * capability, and that sending the field is refused as an unknown field rather
 * than silently dropped. Adding an outlet picker here would therefore build a
 * control whose only possible outcome is `VALIDATION_UNKNOWN_FIELD`. Assigning
 * outlets is `PUT /v1/users/{id}/outlets`, which is FE014's, and the success
 * notice on the list points there.
 */

const NAME_REQUIRED = 'Enter the name to show beside their actions.';

export const PASSWORD_REQUIRED = 'Owners and managers sign in at a desk, so set a password.';
export const PIN_REQUIRED = 'Outlet and gate staff are named at a till by PIN, so set one.';

/**
 * What this dialog will send, and what it refuses before sending.
 *
 * The staff-code and PIN rules are imported. Both were local to a form before
 * FE019 and the PIN's copy had already drifted into a second file — see
 * `lib/field-schemas.ts`, which is now the only place either is written.
 *
 * The password has no rule for the reason it has none anywhere else in this app:
 * the API holds one to a minimum length taken from its environment, and the
 * published document carries no bound, so a rule here would refuse a password
 * the API would have accepted.
 */
const newUserSchema = z
  .object({
    staffCode: staffCodeSchema,
    displayName: z.string().trim().min(1, NAME_REQUIRED),
    role: z.custom<UserRole>(),
    password: z.string(),
    pin: optionalPinSchema,
  })
  /**
   * **Somebody must be able to sign in somewhere.**
   *
   * Which credential is not a matter of taste: a password opens the back office
   * and a PIN names somebody at a till, and the role says which of those this
   * person does. `signsInAtTheDesk` carries the reasoning and the incident.
   *
   * Refused here and not by the server, which accepts a person with neither —
   * so this is stricter than the API, which is the unusual direction. It is
   * fail-safe: the worst case is refusing a creation the API would have taken,
   * and the alternative is what happened, which was a manager who could not sign
   * in and a refusal that read as a wrong password.
   *
   * Not extended to `EditUserDialog`, deliberately. `UserResource` carries no
   * `hasPassword`, so promoting a cashier to manager cannot be checked without
   * forcing a password on people who already have a perfectly good one.
   */
  .superRefine((values, ctx) => {
    if (signsInAtTheDesk(values.role)) {
      if (values.password === '') {
        ctx.addIssue({ code: 'custom', path: ['password'], message: PASSWORD_REQUIRED });
      }

      return;
    }

    if (values.pin === '') {
      ctx.addIssue({ code: 'custom', path: ['pin'], message: PIN_REQUIRED });
    }
  });

type NewUserValues = z.infer<typeof newUserSchema>;

export function NewUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (user: UserResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>New person</DialogTitle>
          <DialogDescription>
            Somebody who signs in — at a desk, at a gate or at a till. The staff code is what they
            type to identify themselves, and it cannot be changed afterwards.
          </DialogDescription>
        </DialogHeader>

        {/* The form is a child so that closing the dialog unmounts it and the
            next open starts empty. Holding the fields in this component would
            leave a half-typed person — and, worse, a typed password — waiting
            the next time somebody opened the form. */}
        <NewUserForm onCreated={onCreated} />
      </FormDialogContent>
    </Dialog>
  );
}

function NewUserForm({ onCreated }: { onCreated: (user: UserResource) => void }) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(newUserSchema),
    mode: 'onTouched',
    defaultValues: {
      staffCode: '',
      displayName: '',
      role: DEFAULT_NEW_USER_ROLE,
      password: '',
      pin: '',
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this form is open. The dialog unmounts on success, so
  // the next attempt gets a fresh one; a refusal rolls the claim back
  // server-side, so a corrected resubmit re-claims cleanly. Where this component
  // makes more than one kind of write, the same key is safe across them: the
  // API's record is unique on `(scope, key)`, and each of those writes is its own
  // scope.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting;

  // Which credential this person cannot go without, following the picker rather
  // than the value at first render — changing the role changes which field is
  // required, and the form has to say so before the submit does.
  const atTheDesk = signsInAtTheDesk(form.watch('role'));

  // Changing the role changes which credential is required, so a refusal already
  // on screen may now be about a field this role does not need. Cleared rather
  // than re-validated: re-running the resolver here would refuse the *other*
  // field the instant somebody switched, before they had a chance to fill it,
  // which is the untouched-field-goes-red behaviour FE019 exists to prevent.
  const { clearErrors } = form;

  useEffect(() => {
    clearErrors(['password', 'pin']);
  }, [atTheDesk, clearErrors]);

  // Watched rather than held in a second copy: the hint under the picker has to
  // follow the value the resolver will actually be given.
  const role = form.watch('role');

  const roleRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = async ({
    staffCode,
    displayName,
    role: chosen,
    password,
    pin,
  }: NewUserValues) => {
    // Belt and braces with the disabled button below. A second submit of the
    // same staff code is refused by the API's uniqueness rule, which reads as
    // "that code is taken" moments after it was accepted — and unlike an outlet
    // code, a staff code taken by the first submit is taken for ever.
    if (pending) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client. Nothing about
      // `POST /v1/users` is written down in this file.
      //
      // `password` and `pin` are spread in only when set, never sent as empty
      // strings. Both are optional on the DTO and an empty string is a
      // credential the API would refuse, not an absent one — and a person who
      // only ever signs in at a terminal is meant to have no password at all.
      const body: UsersController_create_v1Body = {
        staffCode: staffCode.toUpperCase(),
        displayName,
        role: chosen,
        ...(password !== '' ? { password } : {}),
        ...(pin !== '' ? { pin } : {}),
      };

      onCreated(
        await client.request(UsersController_create_v1, {
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IDENTITY_STAFF_CODE_TAKEN') {
        // Beside the field, and the focus goes with it. Somebody who submitted
        // with the keyboard is otherwise left with the caret on the button and
        // a message they have to go and find.
        form.setError('staffCode', { message: messageForFailure(error) });
        form.setFocus('staffCode');
      } else if (error instanceof ApiError && error.code === 'IDENTITY_ROLE_ESCALATION') {
        // The 403 that is about one control. Rendering it as a page-level
        // refusal would say "you cannot create people", which is not what
        // happened — everything else on this form was acceptable.
        //
        // No `setFocus` for this one: the role control is a Select trigger and
        // not a registered input, so there is nothing for react-hook-form to
        // focus. The refusal is described by the trigger instead.
        form.setError('role', { message: messageForFailure(error) });
        roleRef.current?.focus();
      } else {
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared, including the credentials. The retry is one
      // corrected field, and re-typing a password to fix a staff code is a
      // cruelty this form is in no position to inflict.
    }
  };

  return (
    // One column, per docs/ui-conventions.md. `noValidate` because the browser's
    // own bubbles are not this app's error language and cannot be worded.
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="staffCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Staff code
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // A staff code is read aloud and typed back in: mono face,
                    // tabular figures.
                    className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                    required
                    placeholder="BINU05"
                    autoComplete="off"
                    autoCapitalize="characters"
                    {...field}
                    onChange={(event) => {
                      // Uppercased as it is typed, because the API stores it
                      // uppercase. Left alone, somebody types `binu05`, the list
                      // comes back saying `BINU05`, and the screen looks like it
                      // changed their answer.
                      field.onChange(event.target.value.toUpperCase());
                    }}
                  />
                }
              />
              <FormDescription>
                Unique for all time, and never reissued — not even after somebody leaves. It names
                who did something in records that outlive their employment, so a reused code would
                put two people behind one name.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="displayName"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    required
                    placeholder="Binu Mathew"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel id="user-role-label" className={fieldLabel}>
                Role
              </FormLabel>
              {/* Not wrapped in `FormControl`: the trigger is a button rather
                  than the input the generated id belongs on, and it already
                  carries its own accessible name. The picker opens on a role, so
                  the only thing that can go wrong here is the server's
                  escalation refusal. */}
              <Select
                value={role}
                items={USER_ROLE_ITEMS}
                onValueChange={(next) => field.onChange(next as UserRole)}
              >
                <SelectTrigger
                  id="user-role"
                  ref={roleRef}
                  aria-label="Role"
                  aria-invalid={form.formState.errors.role !== undefined}
                  aria-describedby={
                    form.formState.errors.role === undefined ? 'user-role-hint' : 'user-role-error'
                  }
                  className={`${fieldSurface} h-11 w-full`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {USER_ROLES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {labelForRole(option)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {form.formState.errors.role === undefined ? (
                <p id="user-role-hint" className="text-sm text-muted-foreground">
                  {hintForRole(role)}
                </p>
              ) : (
                <FormMessage id="user-role-error" />
              )}
            </FormItem>
          )}
        />

        {/* A quiet inset panel, and the one place the contrast trap lives:
            `text-muted-foreground` on `bg-muted` measures 4.39:1 and misses AA.
            The text here is `text-foreground` for that reason and no other. */}
        <div className="space-y-4 rounded-2xl bg-muted px-4 py-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">A credential to start with</p>
            {/* Stated before either field rather than under them, because it
                changes what somebody types. An owner who believes they are
                choosing a lasting password picks one they intend to keep secret
                and writes it down; an owner who knows it is a handover picks one
                they can say out loud. */}
            <p className="text-sm text-foreground/70">
              {atTheDesk
                ? 'A password signs them in to the back office; the PIN is only needed if they will also be named at a till.'
                : 'A PIN names them at a till; the password is only needed if they will also sign in to the back office.'}{' '}
              Temporary either way — whoever you set one for must replace it the first time they
              sign in, before they can do anything else.
            </p>
          </div>

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel required={atTheDesk} className={fieldLabel}>
                  Back-office password
                </FormLabel>
                <FormControl
                  render={
                    <PasswordInput
                      className={`${fieldSurface} h-11`}
                      placeholder={
                        atTheDesk ? 'At least 12 characters' : 'Leave blank for terminal-only staff'
                      }
                      // `new-password`, so a manager's own saved credential is
                      // not offered into a field that would then set it on
                      // somebody else.
                      autoComplete="new-password"
                      {...field}
                    />
                  }
                />
                <FormDescription className="text-foreground/70">
                  {atTheDesk
                    ? 'For signing in to the back office, which is where this role works.'
                    : 'For signing in to the back office. Leave it blank for staff who only ever sign in at a terminal.'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pin"
            render={({ field }) => (
              <FormItem>
                <FormLabel required={!atTheDesk} className={fieldLabel}>
                  Terminal PIN
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      type="password"
                      className={`${fieldSurface} h-11 font-mono tracking-widest numeric`}
                      placeholder="4827"
                      autoComplete="new-password"
                      inputMode="numeric"
                      {...field}
                    />
                  }
                />
                <FormDescription className="text-foreground/70">
                  {atTheDesk
                    ? '4 to 6 digits. Only needed if they will also be named at a till.'
                    : '4 to 6 digits, typed on a touchscreen at a gate or a till.'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Above the submit and it stays there — a toast vanishes, and somebody
            who looked away has no way to get it back. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Creating…' : 'Create person'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
